import moment from 'moment/moment';
import { FormattedMessage } from 'react-intl';
import {
  FIELD_AMOUNT,
  FIELD_MEMBERS,
  FIELD_TEXT_REQUIRED,
} from './constants.fields';
import getDateAsMoment from 'utils/getDateAsMoment';
import { PAY_FREQUENCY } from 'pages/AffordaWagesAndIncome/constants';

export const MAX_RECORDS_BY_USER = 1;

export const ALIMONY_MIN_AGE = 15;

export const MESSAGE_IDS = {
  title: 'afforda.wagesAndIncome.details.alimony.title',
  description: 'afforda.wagesAndIncome.details.alimony.subtitle',
  addButton: 'afforda.wagesAndIncome.details.alimony.add',
  addCaption: 'afforda.wagesAndIncome.details.alimony.addDescription',
};

export const getDefaultValues = (item) => {
  return {
    ...item,
    id: item.id,
    source: item.source ?? '',
    payFrequency: item.payFrequency ?? '',
    weeklyAmount: item.weeklyAmount ?? '',
    monthlyAmount: item.monthlyAmount ?? '',
    annualAmount: item.annualAmount ?? '',
    endDate: getDateAsMoment(item.endDate),
  };
};

export const ALIMONY_FIELD_NAMES = {
  annualAmount: 'annualAmount',
  endDate: 'endDate',
  memberId: 'memberId',
  monthlyAmount: 'monthlyAmount',
  payFrequency: 'payFrequency',
  paymentsExpectedEndType: 'paymentsExpectedEndType',
  source: 'source',
  weeklyAmount: 'weeklyAmount',
};

export const NEW_ALIMONY = {
  annualAmount: '',
  endDate: '',
  memberId: '',
  monthlyAmount: '',
  payFrequency: '',
  paymentsExpectedEndType: '',
  source: '',
  weeklyAmount: '',
};

export const CALENDAR_DATE = 'date';
export const CALENDAR_ONGOING = 'Ongoing';
export const CALENDAR_NA = 'N/A';

export const PAY_FREQUENCY_OPTIONS = {
  WEEKLY: {
    value: PAY_FREQUENCY.weekly,
    text: 'afforda.wagesAndIncome.details.job.weekly',
  },
  MONTHLY: {
    value: PAY_FREQUENCY.monthly,
    text: 'afforda.wagesAndIncome.details.job.monthly',
  },
  ANNUALLY: {
    value: PAY_FREQUENCY.annually,
    text: 'afforda.wagesAndIncome.details.job.annually',
  },
};

export const DATE_SELECT_OPTIONS = {
  NA: {
    value: CALENDAR_NA,
    text: 'afforda.wagesAndIncome.details.alimony.paymentsExpectedEndType.na',
  },
  ONGOING: {
    value: CALENDAR_ONGOING,
    text: 'afforda.wagesAndIncome.details.alimony.paymentsExpectedEndType.ongoing',
  },
  ANNUALLY: {
    value: CALENDAR_DATE,
    text: 'afforda.wagesAndIncome.details.alimony.paymentsExpectedEndType.date',
  },
};

export const FIELD_MEMBER_SELECTED = {
  watchFields: [ALIMONY_FIELD_NAMES.memberId],
  hidden: ({ getValues }) => !getValues(ALIMONY_FIELD_NAMES.memberId)?.length,
};

export const FIELD_FROM_WHOM = {
  watchFields: [ALIMONY_FIELD_NAMES.source],
  hidden: ({ getValues }) => !getValues(ALIMONY_FIELD_NAMES.source)?.length,
};

export const FIELD_PAY_FREQUENCY = {
  watchFields: [ALIMONY_FIELD_NAMES.memberId, ALIMONY_FIELD_NAMES.payFrequency],
  hidden: ({ getValues }) =>
    !getValues(ALIMONY_FIELD_NAMES.memberId)?.length ||
    !getValues(ALIMONY_FIELD_NAMES.payFrequency)?.length,
};

export const FIELD_PAY_FREQUENCY_WEEKLY = {
  watchFields: [ALIMONY_FIELD_NAMES.memberId, ALIMONY_FIELD_NAMES.payFrequency],
  hidden: ({ getValues }) =>
    !getValues(ALIMONY_FIELD_NAMES.memberId)?.length ||
    getValues(ALIMONY_FIELD_NAMES.payFrequency) !== PAY_FREQUENCY.weekly,
};

export const FIELD_PAY_FREQUENCY_MONTHLY = {
  watchFields: [ALIMONY_FIELD_NAMES.memberId, ALIMONY_FIELD_NAMES.payFrequency],
  hidden: ({ getValues }) =>
    !getValues(ALIMONY_FIELD_NAMES.memberId)?.length ||
    getValues(ALIMONY_FIELD_NAMES.payFrequency) !== PAY_FREQUENCY.monthly,
};

export const FIELD_PAY_FREQUENCY_ANNUALLY = {
  watchFields: [ALIMONY_FIELD_NAMES.memberId, ALIMONY_FIELD_NAMES.payFrequency],
  hidden: ({ getValues }) =>
    !getValues(ALIMONY_FIELD_NAMES.memberId)?.length ||
    getValues(ALIMONY_FIELD_NAMES.payFrequency) !== PAY_FREQUENCY.annually,
};

export const FIELD_GROUP_BUTTONS = {
  variant: 'buttonGroup',
  direction: 'column',
  rules: {
    required: true,
  },
  GridProps: {
    xs: 12,
  },
};

export const FIELDS = ({ disabledMembers, intl, members }) => {
  return [
    ...FIELD_MEMBERS({ members, disabledMembers }),
    {
      ...FIELD_MEMBER_SELECTED,
      ...FIELD_TEXT_REQUIRED,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.alimony.source" />
      ),
      placeholder: intl.formatMessage({
        id: 'afforda.wagesAndIncome.details.alimony.payersFull',
      }),
      name: ALIMONY_FIELD_NAMES.source,
    },

    // PayFreq
    {
      ...FIELD_GROUP_BUTTONS,
      ...FIELD_FROM_WHOM,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.alimony.payFrequency" />
      ),
      name: ALIMONY_FIELD_NAMES.payFrequency,
      rules: {
        required: true,
      },
      options: Object.values(PAY_FREQUENCY_OPTIONS)?.map((option) => ({
        value: option.value,
        label: <FormattedMessage id={option.text} />,
      })),
      GridProps: {
        xs: 12,
      },
      direction: 'row',
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_PAY_FREQUENCY_WEEKLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.alimony.weeklyAmount" />
      ),
      name: ALIMONY_FIELD_NAMES.weeklyAmount,
      GridProps: {
        xs: 12,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_PAY_FREQUENCY_MONTHLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.alimony.monthlyAmount" />
      ),
      name: ALIMONY_FIELD_NAMES.monthlyAmount,
      GridProps: {
        xs: 12,
      },
    },
    {
      ...FIELD_AMOUNT,
      ...FIELD_PAY_FREQUENCY_ANNUALLY,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.alimony.annualAmount" />
      ),
      name: ALIMONY_FIELD_NAMES.annualAmount,
      GridProps: {
        xs: 12,
      },
    },

    {
      ...FIELD_PAY_FREQUENCY,
      ...FIELD_GROUP_BUTTONS,
      name: ALIMONY_FIELD_NAMES.paymentsExpectedEndType,
      label: (
        <FormattedMessage id="afforda.wagesAndIncome.details.alimony.endDate" />
      ),

      rules: {
        required: true,
      },
      options: Object.values(DATE_SELECT_OPTIONS)?.map((option) => ({
        value: option.value,
        label: <FormattedMessage id={option.text} />,
      })),
      GridProps: {
        xs: 12,
      },
      direction: 'row',
    },

    // Received
    {
      watchFields: [ALIMONY_FIELD_NAMES.paymentsExpectedEndType],
      hidden: ({ getValues }) =>
        getValues(ALIMONY_FIELD_NAMES.paymentsExpectedEndType) !== 'date',
      variant: 'date',
      name: ALIMONY_FIELD_NAMES.endDate,
      disablePast: true,
      defaultCalendarMonth: moment(moment().month(), 'mm'),
      rules: {
        minDate: moment().startOf('day'),
      },
    },
  ];
};
